import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';

const MoowooContext = createContext();

export function MoowooProvider({ children }) {
  const [freeAmount, setFreeAmount] = useState(0);
  const [paidAmount, setPaidAmount] = useState(0);
  const [freeExpiresAt, setFreeExpiresAt] = useState(null);
  const [paidExpiresAt, setPaidExpiresAt] = useState(null);
  const [loading, setLoading] = useState(true);

  // 무료 무우 지급 시작일. 서버 연동 시 실제 가입일로 교체할 것.
  const [freeStartDate, setFreeStartDate] = useState(new Date());

  // 자동 반환(refund_expired_moowoo_gifts) 이중 잠금 제어 Ref
  const isRefundRunningRef = useRef(false);
  const lastRefundAtRef = useRef(0);

  const balance = freeAmount + paidAmount;
  // App.jsx 하위 호환 유지
  const setBalance = (v) => setFreeAmount(Number(v) || 0);

  // 만료된 미수령 선물 자동 반환 공용 함수
  const runExpiredGiftRefund = async () => {
    let acquired = false;
    try {
      // 1. 로그인 사용자 확인 (비로그인이면 RPC 호출 차단)
      // getUser()는 매 호출 시 인증 서버로 왕복하여 403을 유발하므로 로컬 세션 조회로 대체
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        return { refunded_count: 0, refunded_total: 0, skipped_count: 0 };
      }

      // 2. 실행 중 잠금 검사
      if (isRefundRunningRef.current) {
        return { refunded_count: 0, refunded_total: 0, skipped_count: 0 };
      }

      // 3. 쿨다운 검사 (60초)
      const now = Date.now();
      if (now - lastRefundAtRef.current < 60000) {
        return { refunded_count: 0, refunded_total: 0, skipped_count: 0 };
      }

      // 4. 선점: RPC 호출 전에 잠금 및 시각 갱신
      isRefundRunningRef.current = true;
      acquired = true;
      lastRefundAtRef.current = now;

      // 5. RPC 호출
      const { data, error } = await supabase.rpc('refund_expired_moowoo_gifts');
      if (error) {
        console.warn('Failed to refund expired moowoo gifts:', error);
        return { refunded_count: 0, refunded_total: 0, skipped_count: 0 };
      }

      // 7. 반환값 반환
      return data || { refunded_count: 0, refunded_total: 0, skipped_count: 0 };
    } catch (err) {
      console.warn('Error in runExpiredGiftRefund:', err);
      return { refunded_count: 0, refunded_total: 0, skipped_count: 0 };
    } finally {
      // 6. 잠금을 직접 획득한 호출만 finally 에서 잠금 해제
      if (acquired) {
        isRefundRunningRef.current = false;
      }
    }
  };

  const refreshBalance = async (checkMounted) => {
    const isSafe = () => (typeof checkMounted === 'function' ? checkMounted() : true);

    try {
      // getUser()는 매 호출 시 인증 서버로 왕복하여 403을 유발하므로 로컬 세션 조회로 대체
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        if (isSafe()) {
          setFreeAmount(0);
          setPaidAmount(0);
          setFreeExpiresAt(null);
          setPaidExpiresAt(null);
          setLoading(false);
        }
        return;
      }

      // [자동 반환 먼저 실행 → 잔액 읽기 나중]
      await runExpiredGiftRefund();

      // 마운트 재검사 (RPC 대기 중 컴포넌트가 언마운트되었는지 확인)
      if (!isSafe()) return;

      const { data, error } = await supabase
        .from('moowoo_balance')
        .select('free_amount, free_expires_at, paid_amount, paid_expires_at')
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (error || !data) {
        console.warn('Failed to fetch moowoo_balance', error);
        if (isSafe()) {
          setFreeAmount(0);
          setPaidAmount(0);
          setFreeExpiresAt(null);
          setPaidExpiresAt(null);
          setLoading(false);
        }
        return;
      }

      if (isSafe()) {
        setFreeAmount(data.free_amount !== null ? data.free_amount : 0);
        setPaidAmount(data.paid_amount !== null ? data.paid_amount : 0);
        setFreeExpiresAt(data.free_expires_at);
        setPaidExpiresAt(data.paid_expires_at);
        setLoading(false);
      }
    } catch (err) {
      console.warn('Error in refreshBalance', err);
      if (isSafe()) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    let isMounted = true;
    const checkMounted = () => isMounted;

    refreshBalance(checkMounted);

    const { data: authListener } = supabase.auth.onAuthStateChange((event) => {
      if (event !== 'INITIAL_SESSION') {
        lastRefundAtRef.current = 0; // 계정 전환 시 쿨다운 초기화
        refreshBalance(checkMounted);
      }
    });

    return () => {
      isMounted = false;
      if (authListener && authListener.subscription) {
        authListener.subscription.unsubscribe();
      }
    };
  }, []);

  return (
    <MoowooContext.Provider
      value={{
        balance,
        setBalance,
        freeStartDate,
        setFreeStartDate,
        freeAmount,
        paidAmount,
        freeExpiresAt,
        paidExpiresAt,
        loading,
        refreshBalance,
        runExpiredGiftRefund
      }}
    >
      {children}
    </MoowooContext.Provider>
  );
}

export function useMoowooContext() {
  const context = useContext(MoowooContext);
  if (!context) {
    throw new Error('useMoowooContext must be used within a MoowooProvider');
  }
  return context;
}
